import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { type ClubInvoiceDto, type InvoiceStatus } from "@tc/shared";
import {
  type BadgeStatus,
  Button,
  Icon,
  Segmented,
  type SegmentedOption,
  StatusBadge,
  Toast,
} from "@/components/ui";
import { useClubFilter } from "@/lib/clubs/club-filter";
import { useFormatters } from "@/lib/i18n/format";
import { recordsApi, runRecordPdf } from "./api";

const STATUS_BADGE: Record<InvoiceStatus, BadgeStatus> = {
  Draft: "enquiry",
  Issued: "proposal",
  Paid: "confirmed",
};
const STATUS_KEY: Record<InvoiceStatus, string> = {
  Draft: "invoice.draft",
  Issued: "records.issued",
  Paid: "records.paid",
};

type StatusFilter = "all" | "Issued" | "Paid";

/**
 * The club records — invoices list (TW-3452). Lists the caller's Issued/Paid invoices across all
 * trips (a club never sees a Draft — the invoice RLS policy guarantees it, so this list is the
 * record), each linking to its trip and offering a branded-PDF download. Mounted at `/invoices` for
 * both audiences via {@link OpsInvoicesPage}/{@link ClubInvoicesPage}; back office can scope to one
 * club via the ClubFilter and sees the club column.
 */
function InvoicesView({ scopeId, showClub }: { scopeId?: string; showClub?: boolean }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<ClubInvoiceDto[] | null>(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [pdfId, setPdfId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  }, []);

  const load = useCallback(async () => {
    setError(false);
    try {
      setInvoices(await recordsApi.listClubInvoices(scopeId));
    } catch {
      setError(true);
    }
  }, [scopeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(
    () => (invoices ?? []).filter((i) => filter === "all" || i.status === filter),
    [invoices, filter],
  );

  const filterOptions: SegmentedOption<StatusFilter>[] = [
    { value: "all", label: t("trips.filterAll") },
    { value: "Issued", label: t("records.issued") },
    { value: "Paid", label: t("records.paid") },
  ];

  const downloadPdf = useCallback(
    async (inv: ClubInvoiceDto) => {
      setPdfId(inv.id);
      try {
        await runRecordPdf(
          () => recordsApi.requestInvoicePdf(inv.tripId, inv.id),
          (exportId) => recordsApi.invoicePdfStatus(inv.tripId, inv.id, exportId),
        );
      } catch {
        flash(t("records.pdfError"));
      } finally {
        setPdfId(null);
      }
    },
    [flash, t],
  );

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t("nav.invoices")}</h1>
        <div className="sub">{t("records.invoicesSub")}</div>
      </div>

      <Segmented options={filterOptions} value={filter} onChange={setFilter} />

      <div style={{ marginTop: "var(--gap)" }}>
        {error ? (
          <div className="empty">{t("records.loadError")}</div>
        ) : invoices === null ? (
          <div className="empty">{t("common.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            <div className="empty-ic">
              <Icon name="invoices" size={22} />
            </div>
            <div style={{ fontWeight: 600 }}>{t("records.noInvoices")}</div>
          </div>
        ) : (
          <div className="rec-list">
            {filtered.map((inv) => (
              <div key={inv.id} className="rec-row">
                <div className="rec-main">
                  <div className="row" style={{ gap: 8, alignItems: "center" }}>
                    <span className="mono">{inv.number}</span>
                    <StatusBadge status={STATUS_BADGE[inv.status]} label={t(STATUS_KEY[inv.status])} />
                  </div>
                  <button className="link-row sub" style={{ marginTop: 4 }} onClick={() => navigate(`/trips/${inv.tripId}`)}>
                    {inv.tripReference}
                    {inv.tripName ? ` · ${inv.tripName}` : ""}
                    {showClub && inv.clubName ? ` · ${inv.clubName}` : ""}
                  </button>
                </div>
                <div className="row" style={{ gap: 12, alignItems: "center" }}>
                  <strong>{fmt.currency(inv.amount, inv.currency)}</strong>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconRight="arrowR"
                    onClick={() => navigate(`/trips/${inv.tripId}/invoices/${inv.id}`)}
                  >
                    {t("factura.open")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="download"
                    disabled={pdfId === inv.id}
                    onClick={() => downloadPdf(inv)}
                  >
                    {pdfId === inv.id ? t("records.generatingPdf") : t("records.downloadPdf")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {toast && <Toast message={toast} />}
    </div>
  );
}

/** Club records — the club's own Issued/Paid invoices (RLS-scoped; no club switcher). */
export function ClubInvoicesPage() {
  return <InvoicesView />;
}

/** Back-office records — every club's invoices, scoped by the ClubFilter; shows the club column. */
export function OpsInvoicesPage() {
  const { scopeId } = useClubFilter();
  return <InvoicesView scopeId={scopeId} showClub />;
}
